import * as XLSX from 'xlsx';

function minutesToHHMM(mins) {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  const hh = String(h).padStart(2, '0');
  return `${hh}:${mm}`;
}

function getISOWeekNumber(date) {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
}

function normalizeStart(date) {
  if (!date) return null;
  const d = new Date(date);
  if (!Number.isFinite(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function normalizeEnd(date) {
  if (!date) return null;
  const d = new Date(date);
  if (!Number.isFinite(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  return d;
}

function sanitizeFileName(raw, fallback) {
  const base = String(raw || fallback || 'planning').trim() || 'planning';
  return base.replace(/[\\/:*?"<>|]/g, '_');
}

export function exportPlanningToExcel({
  events = [],
  planningName,
  locale = 'en-US',
  fallbackName = 'planning',
  userLabelById,
  startDate,
  endDate,
  columnLabels,
}) {
  const labelMap = {
    date: 'Date',
    week: 'Week number',
    weekday: 'Day of the week',
    start: 'Start datetime',
    end: 'End datetime',
    duration: 'Duration',
    effective: 'Effective duration',
    event: 'Event name',
    description: 'Description',
    role: 'Role',
    assigned: 'Assigned user',
    ...columnLabels,
  };

  if (!Array.isArray(events) || events.length === 0) {
    return { status: 'empty' };
  }

  const from = normalizeStart(startDate);
  const to = normalizeEnd(endDate);

  const filtered = events.filter((ev) => {
    if (!ev?.start) return false;
    const start = new Date(ev.start);
    if (!Number.isFinite(start.getTime())) return false;
    if (from && start < from) return false;
    if (to && start > to) return false;
    return true;
  });

  if (!filtered.length) {
    return { status: 'empty' };
  }

  const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const rows = [];
  const labelLookup = userLabelById instanceof Map ? userLabelById : new Map(Object.entries(userLabelById || {}));

  filtered.forEach((ev) => {
    const start = ev?.start ? new Date(ev.start) : null;
    if (!(start && Number.isFinite(start.getTime()))) return;
    const end = ev?.end ? new Date(ev.end) : new Date(start);
    if (!Number.isFinite(end.getTime())) return;
    const dateString = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
    const dayName = weekdayFormatter.format(start);
    const weekNumber = getISOWeekNumber(start);
    const durationMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    const effectiveMinutesRaw = Number(ev?.extendedProps?.effectiveMinutes);
    const effectiveMinutes = Number.isFinite(effectiveMinutesRaw) && effectiveMinutesRaw > 0 ? effectiveMinutesRaw : durationMinutes;
    const rolesRequired = Array.isArray(ev?.extendedProps?.rolesRequired) ? ev.extendedProps.rolesRequired : [];
    const assignments = Array.isArray(ev?.extendedProps?.assignments) ? [...ev.extendedProps.assignments] : [];
    const slots = [];

    if (rolesRequired.length) {
      rolesRequired.forEach((roleRow) => {
        const roleName = roleRow?.role || '';
        const count = Math.max(1, Number(roleRow?.count) || 1);
        for (let i = 0; i < count; i += 1) {
          const matchIdx = assignments.findIndex((a) => (a?.role || '') === roleName && a?.uid);
          const match = matchIdx >= 0 ? assignments.splice(matchIdx, 1)[0] : null;
          slots.push({ role: roleName, uid: match?.uid || null });
        }
      });
    }

    assignments.forEach((a) => {
      if (a) slots.push({ role: a.role || '', uid: a.uid || null });
    });

    if (!slots.length) slots.push({ role: '', uid: null });

    slots.forEach((slot) => {
      const assignedUid = slot?.uid;
      const assignedLabel = assignedUid ? (labelLookup.get(assignedUid) || assignedUid) : '';
      rows.push({
        [labelMap.date]: dateString,
        [labelMap.week]: weekNumber,
        [labelMap.weekday]: dayName,
        [labelMap.start]: new Date(start),
        [labelMap.end]: new Date(end),
        [labelMap.duration]: minutesToHHMM(durationMinutes),
        [labelMap.effective]: minutesToHHMM(effectiveMinutes),
        [labelMap.event]: ev?.title || '',
        [labelMap.description]: ev?.extendedProps?.description || '',
        [labelMap.role]: slot?.role || '',
        [labelMap.assigned]: assignedLabel,
      });
    });
  });

  if (!rows.length) {
    return { status: 'empty' };
  }

  const headers = [
    labelMap.date,
    labelMap.week,
    labelMap.weekday,
    labelMap.start,
    labelMap.end,
    labelMap.duration,
    labelMap.effective,
    labelMap.event,
    labelMap.description,
    labelMap.role,
    labelMap.assigned,
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([]);
  XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'B2' });
  XLSX.utils.sheet_add_json(ws, rows, { origin: 'B3', skipHeader: true, dateNF: 'yyyy-mm-dd hh:mm' });
  const range = { s: { r: 1, c: 1 }, e: { r: rows.length + 1, c: headers.length } };
  ws['!ref'] = XLSX.utils.encode_range(range);
  ws['!cols'] = [
    { wch: 4 },
    { wch: 12 },
    { wch: 12 },
    { wch: 18 },
    { wch: 20 },
    { wch: 20 },
    { wch: 12 },
    { wch: 16 },
    { wch: 30 },
    { wch: 40 },
    { wch: 20 },
    { wch: 26 },
  ];
  ws['!freeze'] = { xSplit: 1, ySplit: 2 };

  const baseName = sanitizeFileName(planningName, fallbackName);
  const today = new Date();
  const isoDate = today.toISOString().slice(0, 10);
  const fileName = `${baseName}-${isoDate}.xlsx`;
  XLSX.utils.book_append_sheet(wb, ws, 'Planning');
  XLSX.writeFile(wb, fileName, { cellDates: true });

  return { status: 'success', fileName, rows: rows.length };
}

